import { countries } from "countries-list";
import dayjs from "dayjs";
import "dayjs/locale/en.js";
import "dayjs/locale/fr.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import duration from "dayjs/plugin/duration.js";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import timezone from "dayjs/plugin/timezone.js";
import updateLocale from "dayjs/plugin/updateLocale.js";
import utc from "dayjs/plugin/utc.js";

//#region src/types.ts
/**
* Locales
*/
let Locale = /* @__PURE__ */ function(Locale) {
	Locale["English"] = "en";
	Locale["French"] = "fr";
	Locale["German"] = "de";
	Locale["Italian"] = "it";
	Locale["Spanish"] = "es";
	return Locale;
}({});
let Currency = /* @__PURE__ */ function(Currency) {
	Currency["USD"] = "USD";
	Currency["EUR"] = "EUR";
	Currency["CAD"] = "CAD";
	Currency["AED"] = "AED";
	Currency["AUD"] = "AUD";
	Currency["BRL"] = "BRL";
	Currency["CHF"] = "CHF";
	Currency["CNY"] = "CNY";
	Currency["DKK"] = "DKK";
	Currency["GBP"] = "GBP";
	Currency["HKD"] = "HKD";
	Currency["HUF"] = "HUF";
	Currency["JPY"] = "JPY";
	Currency["KRW"] = "KRW";
	Currency["MXN"] = "MXN";
	Currency["MYR"] = "MYR";
	Currency["NOK"] = "NOK";
	Currency["NZD"] = "NZD";
	Currency["PHP"] = "PHP";
	Currency["PLN"] = "PLN";
	Currency["RUB"] = "RUB";
	Currency["SEK"] = "SEK";
	Currency["SGD"] = "SGD";
	Currency["TTD"] = "TTD";
	Currency["TWD"] = "TWD";
	return Currency;
}({});
let Languages = /* @__PURE__ */ function(Languages) {
	Languages["English"] = "en";
	Languages["French"] = "fr";
	Languages["German"] = "de";
	Languages["Italian"] = "it";
	Languages["Spanish"] = "es";
	Languages["Dutch"] = "nl";
	Languages["Polish"] = "pl";
	Languages["AncientGreek"] = "grc";
	Languages["Arabic"] = "ar";
	Languages["ChineseSimplified"] = "zh-Hans";
	Languages["ChineseTraditional"] = "zh-Hant";
	Languages["Chinese"] = "zh-cn";
	Languages["Hebrew"] = "he";
	Languages["Japanese"] = "ja";
	Languages["Korean"] = "ko";
	Languages["Thai"] = "th";
	Languages["Indonesian"] = "id";
	Languages["Latin"] = "la";
	Languages["Phyrexian"] = "art-x-phyrexian";
	Languages["PortugueseBrazil"] = "pt-BR";
	Languages["PortuguesePortugal"] = "pt-PT";
	Languages["Russian"] = "ru";
	Languages["Sanskrit"] = "sa";
	Languages["Czech"] = "cs";
	Languages["Hungarian"] = "hu";
	return Languages;
}({});
/**
* Map of languages to their flag codes for the flag-icon-css library
*/
const LANGUAGE_FLAG_CODES = {
	[Languages.English]: "us",
	[Languages.French]: "fr",
	[Languages.German]: "de",
	[Languages.Italian]: "it",
	[Languages.Spanish]: "es",
	[Languages.AncientGreek]: "gr",
	[Languages.Arabic]: "sa",
	[Languages.ChineseSimplified]: "cn",
	[Languages.ChineseTraditional]: "tw",
	[Languages.Chinese]: "cn",
	[Languages.Hebrew]: "il",
	[Languages.Japanese]: "jp",
	[Languages.Korean]: "kr",
	[Languages.Latin]: "va",
	[Languages.Phyrexian]: "xx",
	[Languages.PortugueseBrazil]: "br",
	[Languages.Russian]: "ru",
	[Languages.Sanskrit]: "in",
	[Languages.Dutch]: "nl",
	[Languages.Polish]: "pl",
	[Languages.Czech]: "cz",
	[Languages.Hungarian]: "hu",
	[Languages.Thai]: "th",
	[Languages.Indonesian]: "id",
	[Languages.PortuguesePortugal]: "pt"
};
/**
* Map of currencies to their symbols
*/
const CURRENCY_SYMBOLS = {
	[Currency.USD]: "$",
	[Currency.EUR]: "€",
	[Currency.CAD]: "$",
	[Currency.AED]: "AED",
	[Currency.AUD]: "$",
	[Currency.BRL]: "R$",
	[Currency.CHF]: "CHF",
	[Currency.CNY]: "¥",
	[Currency.DKK]: "kr",
	[Currency.GBP]: "£",
	[Currency.HKD]: "$",
	[Currency.HUF]: "Ft",
	[Currency.JPY]: "¥",
	[Currency.KRW]: "₩",
	[Currency.MXN]: "$",
	[Currency.MYR]: "RM",
	[Currency.NOK]: "kr",
	[Currency.NZD]: "$",
	[Currency.PHP]: "P",
	[Currency.PLN]: "zł",
	[Currency.RUB]: "₽",
	[Currency.SEK]: "kr",
	[Currency.SGD]: "$",
	[Currency.TTD]: "$",
	[Currency.TWD]: "NT$"
};
const currencySymbolSet = new Set(Object.values(CURRENCY_SYMBOLS));
/**
* Returns true when the given string is a known currency symbol (e.g. "$", "€", "£").
*/
function isCurrencySymbol(unit) {
	return currencySymbolSet.has(unit);
}
/**
* Map of currencies to their flag codes for the flag-icon-css library
*/
const CURRENCY_FLAG_CODES = {
	[Currency.USD]: "us",
	[Currency.EUR]: "eu",
	[Currency.CAD]: "ca",
	[Currency.AED]: "ae",
	[Currency.AUD]: "au",
	[Currency.BRL]: "br",
	[Currency.CHF]: "ch",
	[Currency.CNY]: "cn",
	[Currency.DKK]: "dk",
	[Currency.GBP]: "gb",
	[Currency.HKD]: "hk",
	[Currency.HUF]: "hu",
	[Currency.JPY]: "jp",
	[Currency.KRW]: "kr",
	[Currency.MXN]: "mx",
	[Currency.MYR]: "my",
	[Currency.NOK]: "no",
	[Currency.NZD]: "nz",
	[Currency.PHP]: "ph",
	[Currency.PLN]: "pl",
	[Currency.RUB]: "ru",
	[Currency.SEK]: "se",
	[Currency.SGD]: "sg",
	[Currency.TTD]: "tt",
	[Currency.TWD]: "tw"
};
let SupportedMarketplaceCurrency = /* @__PURE__ */ function(SupportedMarketplaceCurrency) {
	SupportedMarketplaceCurrency["USD"] = "USD";
	SupportedMarketplaceCurrency["EUR"] = "EUR";
	SupportedMarketplaceCurrency["GBP"] = "GBP";
	SupportedMarketplaceCurrency["CAD"] = "CAD";
	SupportedMarketplaceCurrency["CHF"] = "CHF";
	SupportedMarketplaceCurrency["SEK"] = "SEK";
	SupportedMarketplaceCurrency["DKK"] = "DKK";
	SupportedMarketplaceCurrency["NOK"] = "NOK";
	SupportedMarketplaceCurrency["PLN"] = "PLN";
	SupportedMarketplaceCurrency["HUF"] = "HUF";
	return SupportedMarketplaceCurrency;
}({});
/**
* List of European countries that use EUR currency
* Used to display individual country flags instead of EU flag
*/
const EUR_COUNTRIES = [
	"at",
	"be",
	"cy",
	"ee",
	"fi",
	"fr",
	"de",
	"gr",
	"hr",
	"ie",
	"it",
	"lv",
	"lt",
	"lu",
	"mt",
	"nl",
	"pt",
	"sk",
	"si",
	"es"
];
/**
* Map country codes (uppercase) to supported marketplace currencies.
* This is the single source of truth for country → currency resolution,
* shared between frontend and backend.
*/
const COUNTRY_TO_MARKETPLACE_CURRENCY = {
	US: SupportedMarketplaceCurrency.USD,
	CA: SupportedMarketplaceCurrency.USD,
	AT: SupportedMarketplaceCurrency.EUR,
	BE: SupportedMarketplaceCurrency.EUR,
	CY: SupportedMarketplaceCurrency.EUR,
	DE: SupportedMarketplaceCurrency.EUR,
	EE: SupportedMarketplaceCurrency.EUR,
	ES: SupportedMarketplaceCurrency.EUR,
	FI: SupportedMarketplaceCurrency.EUR,
	FR: SupportedMarketplaceCurrency.EUR,
	GR: SupportedMarketplaceCurrency.EUR,
	HR: SupportedMarketplaceCurrency.EUR,
	IE: SupportedMarketplaceCurrency.EUR,
	IT: SupportedMarketplaceCurrency.EUR,
	LT: SupportedMarketplaceCurrency.EUR,
	LU: SupportedMarketplaceCurrency.EUR,
	LV: SupportedMarketplaceCurrency.EUR,
	MT: SupportedMarketplaceCurrency.EUR,
	NL: SupportedMarketplaceCurrency.EUR,
	PT: SupportedMarketplaceCurrency.EUR,
	SI: SupportedMarketplaceCurrency.EUR,
	SK: SupportedMarketplaceCurrency.EUR,
	GB: SupportedMarketplaceCurrency.GBP,
	CH: SupportedMarketplaceCurrency.CHF,
	SE: SupportedMarketplaceCurrency.SEK,
	DK: SupportedMarketplaceCurrency.DKK,
	NO: SupportedMarketplaceCurrency.NOK,
	PL: SupportedMarketplaceCurrency.PLN,
	HU: SupportedMarketplaceCurrency.HUF
};
/**
* Get the marketplace currency for a country code (case-insensitive).
* Returns null if the country is not supported.
*/
function getCurrencyForCountry(countryCode) {
	return COUNTRY_TO_MARKETPLACE_CURRENCY[countryCode.toUpperCase()] ?? null;
}
const LOCALES_CURRENCY_MAP = {
	"en-US": Currency.USD,
	"en-GB": Currency.GBP,
	"en-CA": Currency.CAD,
	"en-AU": Currency.AUD,
	"en-NZ": Currency.NZD,
	"en-SG": Currency.SGD,
	"en-HK": Currency.HKD,
	"en-PH": Currency.PHP,
	"fr-FR": Currency.EUR,
	"fr-CH": Currency.CHF,
	"fr-CA": Currency.CAD,
	"de-DE": Currency.EUR,
	"de-CH": Currency.CHF,
	"de-AT": Currency.EUR,
	"it-IT": Currency.EUR,
	"it-CH": Currency.CHF,
	"es-ES": Currency.EUR,
	"es-MX": Currency.MXN,
	"pt-BR": Currency.BRL,
	"pt-PT": Currency.EUR,
	"ru-RU": Currency.RUB,
	"zh-CN": Currency.CNY,
	"zh-TW": Currency.TWD,
	"zh-HK": Currency.HKD,
	"ja-JP": Currency.JPY,
	"ko-KR": Currency.KRW,
	"ar-SA": Currency.AED,
	"ar-AE": Currency.AED,
	"pl-PL": Currency.PLN,
	"sv-SE": Currency.SEK,
	"da-DK": Currency.DKK,
	"no-NO": Currency.NOK,
	"ms-MY": Currency.MYR,
	"tr-TT": Currency.TTD,
	en: Currency.USD,
	fr: Currency.EUR,
	de: Currency.EUR,
	it: Currency.EUR,
	es: Currency.EUR,
	pt: Currency.EUR,
	ru: Currency.RUB,
	zh: Currency.CNY,
	ja: Currency.JPY,
	ko: Currency.KRW,
	ar: Currency.AED,
	pl: Currency.PLN,
	sv: Currency.SEK,
	da: Currency.DKK,
	no: Currency.NOK,
	ms: Currency.MYR,
	tr: Currency.TTD
};
/**
* Display currency for a visitor before they pick one: the marketplace
* currency of `countryCode` when we serve that country, else the currency of
* `locale`, else USD.
*
* @param countryCode - ISO 3166-1 alpha-2 code, in any case.
* @param locale - Locale used as fallback when the country is missing or unknown.
*/
function defaultCurrencyForCountry(countryCode, locale) {
	const countryCurrency = countryCode ? getCurrencyForCountry(countryCode) : null;
	if (countryCurrency) return Currency[countryCurrency];
	return (locale ? LOCALES_CURRENCY_MAP[locale] : void 0) ?? Currency.USD;
}

//#endregion
//#region src/config.ts
const supportedLocales = [Locale.English, Locale.French];
const config = {
	locales: supportedLocales,
	defaultLocale: Locale.English,
	defaultCurrency: "USD",
	nextjsLocaleCookieName: "NEXT_LOCALE"
};

//#endregion
//#region src/countries.ts
/**
* All ISO 3166-1 alpha-2 country codes (uppercase)
*/
const ALL_COUNTRY_CODES = Object.keys(countries);
const SUPPORTED_COUNTRY_LOCALES = Object.values(Locale);
function isSupportedLocale(locale) {
	return SUPPORTED_COUNTRY_LOCALES.includes(locale);
}
/**
* Get all countries with localized names using Intl.DisplayNames
* @param locale - The locale for country names (e.g., Locale.English, Locale.French)
* @returns Array of country options sorted alphabetically by name
*/
function getAllCountries(locale) {
	const effectiveLocale = isSupportedLocale(locale) ? locale : Locale.English;
	const displayNames = new Intl.DisplayNames([effectiveLocale], { type: "region" });
	return ALL_COUNTRY_CODES.map((code) => ({
		code: code.toUpperCase(),
		name: displayNames.of(code.toUpperCase()) || code
	})).sort((a, b) => a.name.localeCompare(b.name, effectiveLocale));
}
/**
* Get a single country name by code using Intl.DisplayNames
* @param code - ISO 3166-1 alpha-2 code (e.g., "US", "FR")
* @param locale - The locale for the country name
* @returns The localized country name or the code if not found
*/
function getCountryName(code, locale) {
	const effectiveLocale = isSupportedLocale(locale) ? locale : Locale.English;
	return new Intl.DisplayNames([effectiveLocale], { type: "region" }).of(code.toUpperCase()) || code;
}

//#endregion
//#region src/dayjs.ts
let isInitialized = false;
/**
* Configure the shared Day.js instance with plugins (once) and set the active locale.
*
* Call this early in app boot (wrapped by DayjsProvider).
* Plugins: localizedFormat, relativeTime, updateLocale, utc, timezone, customParseFormat.
*
* @param locale Locale.
* @example
* configureDayjs("fr")
*/
function configureDayjs(locale) {
	if (!isInitialized) {
		dayjs.extend(localizedFormat);
		dayjs.extend(relativeTime);
		dayjs.extend(duration);
		dayjs.extend(updateLocale);
		dayjs.extend(utc);
		dayjs.extend(timezone);
		dayjs.extend(customParseFormat);
		isInitialized = true;
	}
	dayjs.locale(locale);
}
/**
* Returns a relative time string (e.g., "2 hours ago", "in 3 days").
* Wrapper around dayjs().fromNow() to avoid exposing dayjs types to consumers.
*/
function fromNow(date, withoutSuffix) {
	if (date == null) return "";
	return dayjs(date).fromNow(withoutSuffix);
}
/**
* Formats a date in UTC, so the server and every browser render the same
* calendar day whatever their time zone.
* Wrapper around dayjs.utc() to avoid type augmentation issues.
*/
function formatUtc(date, template, locale) {
	const value = dayjs.utc(date);
	return (locale ? value.locale(locale) : value).format(template);
}
/**
* Returns a human-readable duration string (e.g., "2 hours", "30 minutes").
* Wrapper around dayjs.duration().humanize() to avoid type augmentation issues.
*/
function humanizeDuration(milliseconds) {
	return dayjs.duration(milliseconds).humanize();
}
const COMPACT_UNITS = {
	en: {
		s: "<1m",
		m: "m",
		h: "h",
		d: "d",
		mo: "mo",
		y: "y"
	},
	fr: {
		s: "<1 min",
		m: " min",
		h: " h",
		d: " j",
		mo: " mo",
		y: " a"
	}
};
/**
* Returns a compact relative time string (e.g., "22m", "3h", "2d", "1mo", "1y").
* Localized based on the provided or current dayjs locale. Useful for space-constrained UIs like chat previews.
*/
function compactFromNow(date, locale) {
	if (date == null) return "";
	const units = COMPACT_UNITS[locale ?? dayjs.locale()] ?? COMPACT_UNITS.en;
	const now = dayjs();
	const then = dayjs(date);
	if (now.diff(then, "second") < 60) return units.s;
	const diffMinutes = now.diff(then, "minute");
	if (diffMinutes < 60) return `${diffMinutes}${units.m}`;
	const diffHours = now.diff(then, "hour");
	if (diffHours < 24) return `${diffHours}${units.h}`;
	const diffDays = now.diff(then, "day");
	if (diffDays < 30) return `${diffDays}${units.d}`;
	const diffMonths = now.diff(then, "month");
	if (diffMonths < 12) return `${diffMonths}${units.mo}`;
	return `${now.diff(then, "year")}${units.y}`;
}

//#endregion
//#region src/languages.ts
/**
* Fallback display names for language codes that `Intl.DisplayNames`
* doesn't recognise (constructed / artistic codes like Phyrexian) or
* where we want a friendlier label than the standard rendering.
*/
const FALLBACK_LANGUAGE_NAMES = {
	[Languages.Phyrexian]: "Phyrexian",
	[Languages.AncientGreek]: "Ancient Greek"
};
/**
* Returns a human-readable name for a language code (e.g. "en" → "English",
* "fr-FR" → "French", "art-x-phyrexian" → "Phyrexian").
*
* Localized via the optional `displayLocale` argument — pass the current UI
* locale to get names in the user's language ("Anglais" instead of "English"
* when displayLocale is "fr").
*
* Falls back to the raw code when nothing better can be produced.
*/
const getLanguageDisplayName = (code, displayLocale = Locale.English) => {
	if (!code) return "";
	const fallback = FALLBACK_LANGUAGE_NAMES[code];
	if (fallback) return fallback;
	try {
		const name = new Intl.DisplayNames([displayLocale], { type: "language" }).of(code);
		if (name && name.toLowerCase() !== code.toLowerCase()) return name;
	} catch {}
	return code;
};
/**
* Returns `"English (en)"` — name first, code in parens. Use when the user
* needs to see both the human label and the raw value side-by-side
* (e.g. dropdowns, badges in the CSV importer error groups).
*/
const formatLanguageWithCode = (code, displayLocale = Locale.English) => {
	if (!code) return "";
	const name = getLanguageDisplayName(code, displayLocale);
	if (!name || name === code) return code;
	return `${name} (${code})`;
};
/**
* Picks the language a new inventory line, list item or listing should use
* for a product: the preferred language when the product exists in it, then
* the fallback (English by default) when the product exists in it, then the
* product's first language.
*
* An empty or missing `availableLanguages` means the product's languages are
* unknown, so the preferred language (or the fallback) is returned unchanged.
*/
const resolveAvailableLanguage = (preferredLanguage, availableLanguages, fallbackLanguage = Languages.English) => {
	const [firstLanguage] = availableLanguages ?? [];
	if (!firstLanguage || !availableLanguages) return preferredLanguage || fallbackLanguage;
	if (preferredLanguage && availableLanguages.includes(preferredLanguage)) return preferredLanguage;
	if (availableLanguages.includes(fallbackLanguage)) return fallbackLanguage;
	return firstLanguage;
};

//#endregion
//#region src/i18n/en.json
var en_default = {
	global: {
		"addProductMethods": {
			"addGraded": {
				"description": "Add graded cards to your inventory by searching their name",
				"title": "Add graded card"
			},
			"bulkImport": {
				"description": "Create an import to bulk upload your inventory (with CSV, Text Edit, etc.)",
				"title": "Bulk import"
			},
			"cardScanner": {
				"description": "Scan your cards with your phone camera to add them instantly. Available on the CardNexus mobile app.",
				"title": "Card scanner"
			},
			"criteriaBuilder": {
				"description": "Find and add entire groups of cards based on set, finish, type, color, etc.",
				"title": "Add by filter"
			},
			"csv": {
				"description": "Upload a collection file (CSV) from another service or spreadsheet.",
				"title": "Import a file"
			},
			"manual": {
				"description": "Explore the full product catalog for <bold>{gameName}</bold>, then filter and select the ones you own.",
				"title": "Browse the catalog"
			},
			"preset": {
				"description": "Perfect if you collect complete sets, playsets, master sets",
				"title": "Defined set"
			},
			"search": {
				"description": "Add specific products one-by-one by searching their name",
				"title": "Quick search"
			},
			"text": {
				"description": "Copy and paste a deck or list to add all cards instantly.",
				"title": "Paste a list"
			}
		},
		"common": {
			"featured": "Featured",
			"myGames": "My games",
			"otherGames": "Other games",
			"accessDenied": "Access denied",
			"account": "Account",
			"add": "Add",
			"addCards": "Add products",
			"addCardsFromCsv": "Add cards from csv",
			"addCardsFromPhotos": "Add cards from photos",
			"addCardsFromPreset": "Add cards from preset",
			"addCardsFromText": "Add cards from text",
			"addCardsToInventory": "Add products to inventory",
			"addCardsToList": "Add products to list",
			"addCardsWithCriteriaBuilder": "Add cards by filters",
			"addMoreCards": "Add more products",
			"addOneToImport": "Add 1 to import",
			"addOneToInventory": "Add 1 to inventory",
			"addOneToList": "Add 1 to list",
			"addPicture": "Add picture",
			"addProducts": "Add products",
			"addToCart": "Add to cart",
			"addToImport": "Add to import",
			"addToInventory": "Add to inventory",
			"addToList": "Add to list",
			"addVariantToInventory": "Add variant to inventory",
			"advancedSettings": "Advanced settings",
			"allRightsReserved": "All rights reserved",
			"and": "and",
			"apply": "Apply",
			"asc": "Ascending",
			"availability": "Availability",
			"availableForAllGames": "Available for all games",
			"back": "Back",
			"backToExplore": "Back to explore",
			"backToHomepage": "Back to homepage",
			"backToImport": "Back to import",
			"backToInventory": "Back to inventory",
			"backToList": "Back to list",
			"backToTop": "Back to top",
			"backToSearch": "Back to search",
			"binder": "Binder",
			"blue": "Blue",
			"box": "Box",
			"browse": "Browse",
			"buyer": "Buyer",
			"cancel": "Cancel",
			"cardCount": "{count, plural, =0 {No cards} =1 {1 card} other {# cards}}",
			"sealedCount": "{count, plural, =0 {No sealed} =1 {1 sealed} other {# sealed}}",
			"cards": "{count, plural, =0 {card} =1 {card} other {cards}}",
			"cardsInAttribute": "{count, plural, =1 {# card in {attribute}} other {# cards in {attribute}}}",
			"cardsSelected": "{count, plural, =0 {No card selected} =1 {Card selected} other {Cards selected}}",
			"certification": "Certification",
			"clear": "Clear",
			"clearSearch": "Clear search",
			"close": "Close",
			"collection": "Collection",
			"colors": "Colors",
			"columns": "Columns",
			"columnName": "Name",
			"columnShown": "Shown",
			"showHideColumns": "Columns displayed",
			"pinColumns": "Pin columns",
			"pinLeft": "Pin left",
			"pinRight": "Pin right",
			"resetColumns": "Reset columns",
			"comingSoon": "Coming soon",
			"commonProperties": "Common properties",
			"conditions": "{count, plural, =1 {condition} other {conditions}}",
			"connectAccount": "Connect account",
			"confirm": "Confirm",
			"containerName": "Container name",
			"continue": "Continue",
			"copies": "{count, plural, =0 {# copies} =1 {# copy} other {# copies}}",
			"copy": "Copy",
			"copyLink": "Copy link",
			"copyToClipboard": "Copy to clipboard",
			"copying": "Copying",
			"couldntLoadLocations": "Couldn't load locations",
			"create": "Create",
			"createList": "Create list",
			"createNewContainer": "Create new container",
			"createdOn": "Created on",
			"criteriaBuilder": "Criteria builder",
			"csvImport": "CSV import",
			"currencyWarning": "Prices are sourced from {priceSource} in {priceSourceCurrency}, but displayed in {displayCurrency}. Exchange rates may affect accuracy.",
			"current": "Current",
			"date": "Date",
			"deck": "Deck",
			"default": "Default",
			"delete": "Delete",
			"deleteAll": "Delete all",
			"desc": "Descending",
			"description": "Description",
			"deselectAll": "Deselect all",
			"details": "Details",
			"differentAttributes": "{count, plural, =1 {# {attribute} for this card} other {# different {attribute}s for this card}}",
			"differentCopies": "{count, plural, =0 {No different copy} =1 {1 different copy} other {# different copies}}",
			"differentFinishes": "{count, plural, =0 {No different finish} =1 {1 finish} other {# different finishes}}",
			"disconnect": "Disconnect",
			"collect": "Collect",
			"display": "Display",
			"dontSave": "Don't save",
			"download": "Download",
			"downloadAsCsv": "Download as CSV",
			"downloadAsJson": "Download as JSON",
			"dragToReorder": "Drag to reorder",
			"edit": "Edit",
			"equalTo": "Equal to",
			"error": "Error",
			"errorDescription": "Something went wrong. Please try again or contact support if the problem persists.",
			"expired": "Expired",
			"export": "Export",
			"exportAsCsv": "Export as CSV",
			"exportAsCsvOrJson": "Export as CSV/JSON",
			"exportAsList": "Export as List",
			"exportCompleted": "Export completed successfully",
			"exportData": "Export Data",
			"exportFailed": "Export failed",
			"exportPreviewFailed": "Export preview failed",
			"exportingData": "Exporting Data",
			"finishes": "{count, plural, =0 {No finishes} =1 {1 finish} other {# finishes}}",
			"filters": "Filters",
			"for": "for",
			"forSale": "For sale",
			"forSaleWithPrice": "For sale · {sellPrice}",
			"gameSpecificFields": "{game} specific fields",
			"games": "{count, plural, =0 {game} =1 {game} other {games}}",
			"gallery": "Gallery",
			"generating": "Generating",
			"goToHomepage": "Go to homepage",
			"goToTheApp": "Go to the app",
			"grade": "Grade",
			"grader": "Grader",
			"greaterThan": "Greater than",
			"green": "Green",
			"grid": "Grid",
			"group": "Group",
			"groupBy": "Group by",
			"hideOutOfStock": "In stock only",
			"high": "High",
			"hold": "Hold",
			"hotDeals": "Hot deals",
			"icon": "Icon",
			"locationName": "Location name",
			"noLocationsFound": "No locations found",
			"pink": "Pink",
			"red": "Red",
			"searchLocations": "Search locations",
			"selectLocationOrCreate": "Select a location or create a new one",
			"selectAllTagsOrCreate": "Select all tags or create one",
			"createCustomTag": "Create custom tag",
			"tagName": "Tag name",
			"defaultTags": {
				"onLoan": "Borrowed",
				"investment": "Investment",
				"treasure": "Treasure",
				"forTrade": "For Trade",
				"toGrade": "To Grade",
				"toList": "To List"
			},
			"todayHotDeals": "Today's hot deals",
			"seeAllHotDeals": "See all hot deals",
			"explorePerGame": "Explore by game",
			"identifier": "Identifier",
			"in": "In",
			"invalid": "Invalid",
			"itemsPerPage": "Items per page",
			"languages": "{count, plural, =1 {language} other {languages}}",
			"lastUpdated": "Last updated",
			"lessThan": "Less than",
			"line": "Line",
			"list": "List",
			"listForSale": "List for sale",
			"addForSale": "Add for sale",
			"cancelListing": "Cancel listing",
			"listings": "Listings",
			"putForSale": "Put for sale",
			"individual": "Individual",
			"proSellerBadge": "Pro",
			"professional": "Professional",
			"notGraded": "Not graded",
			"graded": "Graded",
			"lists": "Lists",
			"loading": "Loading",
			"low": "Low",
			"manageAccount": "Manage account",
			"manualSearch": "Manual search",
			"manualSelection": "Manual selection",
			"marketActivity": "Market activity",
			"marketOverview": "Market overview",
			"marketValue": "Market value",
			"fairPrice": "Fair Price",
			"fairPriceTooltip": "Calculated from multiple data sources, taking the card's condition, language and finish into account.",
			"sellValue": "Sell value",
			"buy": "Buy",
			"justListed": "Just listed",
			"hotPrice": "Hot price",
			"sortNewest": "Newest",
			"sortPrice": "Price",
			"sortGrade": "Grade",
			"marketplaceWarning": "Our team is working on adding {marketplace} price data for {game}",
			"max": "Max",
			"maxQuantityInCart": "Maximum quantity already added to cart",
			"regionMismatch": "This seller is in a different region than items in your cart",
			"regionMismatchAlert": "<bold>You have items from {region} sellers in your cart.</bold> Listings from the other region are disabled. <checkoutLink>Checkout first</checkoutLink> to add items from a different region.",
			"mid": "Mid",
			"min": "Min",
			"myCollection": "My collection",
			"next": "Next",
			"no": "No",
			"noDataAvailable": "No data available",
			"noMoreResults": "No more results",
			"noOptionsAvailable": "No options available",
			"noOptionsDescription": "There are no options to select from",
			"noProductsFound": "No products found",
			"noResultDescription": "You can reset your filters by clicking down below.",
			"noResults": "No results",
			"noResultsDescription": "Try adjusting your search terms",
			"none": "None",
			"notAvailable": "Not available",
			"notAvailableShort": "N/A",
			"of": "of",
			"off": "Off",
			"listingsFrom": "{count, plural, =1 {1 listing from} other {# listings from}}",
			"on": "On",
			"oops": "Oops, something went wrong",
			"otherPrintings": "{count, plural, =0 {No other printing} =1 {# other printing} other {# other printings}}",
			"outOfStock": "Out of stock",
			"pageNotFound": "Page not found",
			"pendingUpdates": "{count, plural, =0 {No pending updates} =1 {1 pending update} other {# pending updates}}",
			"pictures": "Pictures",
			"points": "{count, plural, =0 {No points} =1 {1 point} other {# points}}",
			"preferences": "Preferences",
			"preparingDownload": "Preparing download...",
			"previous": "Previous",
			"price": "Price",
			"primary": "Primary",
			"printNumber": "Print number",
			"printing": "Printing",
			"private": "Private",
			"processingExport": "Processing export...",
			"product": "Product",
			"productCount": "{count, plural, =0 {No products} =1 {1 product} other {# products}}",
			"productDetails": "Product details",
			"priceLabel": "Price",
			"products": "{count, plural, =0 {product} =1 {product} other {products}}",
			"profile": "Profile",
			"public": "Public",
			"quantity": "Quantity",
			"referrals": "Referrals",
			"releasedOn": "Released on {date}",
			"remove": "Remove",
			"required": "Required",
			"refineFilters": "Refine filters",
			"resendCode": "Resend Code",
			"reset": "Reset",
			"resetFilters": "Reset filters",
			"resetToDefaults": "Reset to defaults",
			"results": "{count, plural, =0 {result} =1 {result} other {results}}",
			"resultsCount": "{count, plural, =0 {0 results} =1 {1 result} other {{count} results}}",
			"searchDepthNoticeDescription": "Narrow your search with filters to reach the rest.",
			"searchDepthNoticeLastPageTitle": "This is the last page that can be browsed",
			"retry": "Retry",
			"reviews": "{count, plural, =0 {No reviews} =1 {1 review} other {# reviews}}",
			"save": "Save",
			"saved": "Saved",
			"saving": "Saving",
			"search": "Search for a value",
			"seller": "Seller",
			"seeAll": "See all",
			"seeAllPrintings": "See all printings",
			"seeAllSealedProducts": "See all sealed products",
			"seeMoreResults": "See more results",
			"select": "Select",
			"selectAll": "Select all",
			"selectColumns": "Select columns",
			"selectOptions": "Select options",
			"sellPrice": "Sell price",
			"sellPriceTooltip": "The sell price per individual card in this list",
			"settings": "Settings",
			"moreActions": "More actions",
			"shareList": "Share list",
			"show": "Show",
			"showLess": "Show less",
			"showMore": "Show {count} more",
			"showing": "Showing",
			"signIn": "Sign in",
			"signInShort": "Sign in",
			"signOut": "Sign out",
			"signUp": "Sign up",
			"sort": "Sort",
			"sortBy": "Sort by",
			"sortDirection": "Sort direction",
			"sortOrder": "Sort order",
			"status": "Status",
			"termsAndConditions": "Terms and conditions",
			"textImport": "Text import",
			"toComplete": "To complete",
			"totalValue": "Total value",
			"unknown": "Unknown",
			"update": "Update",
			"uploadPicture": "Upload picture",
			"unverified": "Unverified",
			"user": "User",
			"valid": "Valid",
			"validate": "Validate",
			"validating": "Validating",
			"value": "Value",
			"verify": "Verify",
			"viewAll": "View all",
			"viewAction": "View",
			"viewAs": "View as",
			"view": "View",
			"viewDetails": "View details",
			"viewInventory": "View inventory",
			"viewList": "View list",
			"visibility": "Visibility",
			"wantPrice": "Want price",
			"wantPriceTooltip": "The price you want to pay per individual card in this list",
			"white": "White",
			"withBulkImport": "Bulk import",
			"withCSVImport": "Import from file",
			"withCriteriaBuilder": "Add by filter",
			"withPreset": "Use a preset",
			"withQuickSearch": "Quick search",
			"withTextEdit": "Paste a list",
			"yellow": "Yellow",
			"yes": "Yes",
			"you": "You",
			"zoomLevel": "Zoom level",
			"zoomLevelHint": "Card size in grid and gallery views",
			"zoomedIn": "zoomed in",
			"zoomedOut": "zoomed out",
			"new": "New"
		},
		"countries": {
			"us": "United States",
			"gb": "United Kingdom",
			"ca": "Canada",
			"au": "Australia",
			"nz": "New Zealand",
			"eu": "European Union",
			"at": "Austria",
			"be": "Belgium",
			"cy": "Cyprus",
			"ee": "Estonia",
			"fi": "Finland",
			"fr": "France",
			"de": "Germany",
			"gr": "Greece",
			"hr": "Croatia",
			"hu": "Hungary",
			"ie": "Ireland",
			"it": "Italy",
			"lv": "Latvia",
			"lt": "Lithuania",
			"lu": "Luxembourg",
			"mt": "Malta",
			"nl": "Netherlands",
			"pt": "Portugal",
			"sk": "Slovakia",
			"si": "Slovenia",
			"es": "Spain",
			"ch": "Switzerland",
			"pl": "Poland",
			"se": "Sweden",
			"dk": "Denmark",
			"no": "Norway"
		},
		"errors": {
			"confirmPassword": "Passwords must match",
			"copy": "Failed to copy to clipboard",
			"emailInvalid": "Please enter a valid email address",
			"fileUploadFailed": "Failed to upload file. Please try again.",
			"forbidden": "You are not allowed to access this resource",
			"forbiddenTitle": "Forbidden",
			"maintenance": "The service is currently under maintenance. Please try again later.",
			"maintenanceTitle": "Maintenance Mode",
			"maxChars": "Please enter {count} characters or fewer",
			"maxValue": "Please enter a value no greater than {max}",
			"minChars": "Please enter at least {count} characters",
			"minValue": "Please enter a value of at least {min}",
			"mustSelectLocation": "You must pick a location from the dropdown",
			"network": "A network error occurred. Please try again later.",
			"networkTitle": "Network error",
			"notFound": "The resource you are looking for does not exist",
			"notFoundTitle": "Page not found",
			"password": "Password must be at least 8 characters",
			"rateLimit": "You have made too many requests. Please try again later.",
			"rateLimitTitle": "Rate limit exceeded",
			"rateLimitWithTime": "You have made too many requests. Please try again later. You can retry in {seconds} seconds.",
			"required": "This field is required",
			"resetMyAccountConfirmation": "Must type 'reset my account' to confirm",
			"selectAtLeast": "Please select at least {count} options",
			"selectAtLeastOne": "Please select at least one option",
			"server": "An error occurred on the server. Please try again later.",
			"serverTitle": "Server error",
			"timeout": "The request took too long to complete. Please try again.",
			"timeoutTitle": "Timeout",
			"unauthorized": "You are not authorized to access this resource",
			"unauthorizedTitle": "Unauthorized",
			"unknown": "An unknown error occurred. Please try again later.",
			"unknownTitle": "Unknown error",
			"validation": "The input you provided is invalid",
			"validationTitle": "Validation error",
			"copyForSupport": "Copy error for support",
			"certificationRequired": "Certification number is required for graded cards",
			"copied": "Copied!"
		},
		"labels": {
			"alreadyInList": "Already in list",
			"city": "City",
			"confirmPassword": "Confirm password",
			"currency": "Currency",
			"currentPassword": "Current password",
			"description": "Description",
			"email": "Email address",
			"language": "Language",
			"layout": "Layout",
			"location": "Location",
			"marketplace": "Price source",
			"name": "Name",
			"newPassword": "New password",
			"password": "Password",
			"presetType": "Preset type",
			"profile": "Profile",
			"signOutOfOtherSessions": "Sign out of all other devices after password change",
			"username": "Username",
			"value": "Value",
			"amount": "Amount"
		},
		"locale": {
			"description": "The locale of the platform",
			"name": "Display language",
			"placeholder": "Choose your platform language",
			"values": {
				"de": "German",
				"en": "English",
				"es": "Spanish",
				"fr": "French",
				"it": "Italian"
			}
		},
		"maintenance": {
			"title": "We're currently under maintenance",
			"messageFromTeam": "Message from the team",
			"startsAt": "Starts at",
			"endsAt": "Expected end",
			"autoRefresh": "This page will automatically refresh when maintenance is complete.",
			"banner": { "message": "CardNexus will be down for maintenance on <time>{datetime}</time> for <strong>{duration}</strong>" }
		},
		"siteAlert": { "dismiss": "Dismiss" },
		"clientPass": {
			"title": "One quick check before you continue",
			"description": "CardNexus needs to confirm that a person is using this browser. Complete the check below and the page carries on as soon as it passes."
		},
		"periods": {
			"allTime": "All time",
			"oneWeek": "1 week",
			"oneYear": "1 year",
			"sixMonths": "6 months",
			"thirtyDays": "30 days",
			"threeMonths": "3 months"
		},
		"marketplaceOptions": {
			"Cardmarket": "Cardmarket",
			"TCGplayer": "TCGplayer"
		},
		"placeholders": {
			"allGames": "All games",
			"confirmPassword": "Confirm new password",
			"currency": "Select your preferred currency",
			"currentPassword": "Enter current password",
			"email": "Ex: abc@xyz.com",
			"enterValue": "Enter value",
			"location": "Search for a location...",
			"marketplace": "Select a price source",
			"newPassword": "Enter new password",
			"password": "Enter password",
			"search": "Search",
			"searchUsernameOrOrder": "Search username or order number",
			"selectExpansion": "Select an expansion",
			"selectGame": "Select a game",
			"selectGroupBy": "Select group by option",
			"selectStatus": "Select a status",
			"username": "Ex: jacethemindsculptor"
		},
		"productTypes": {
			"all": "All products",
			"sealed": "{count, plural, =0 {No sealed product} =1 {Sealed product} other {Sealed products}}",
			"card": "{count, plural, =0 {No single card} =1 {Single card} other {Single cards}}"
		},
		"sortValues": {
			"bestMatch": "Best match",
			"cardCount": "Card count",
			"sealedProductCount": "Sealed product count",
			"code": "Set code",
			"completion": "Completion",
			"createdAt": "Created at",
			"releaseDate": "Release date",
			"updatedAt": "Updated at"
		},
		"storeBadge": {
			"downloadOn": "Download on the",
			"appStore": "App Store",
			"getItOn": "Get it on",
			"googlePlay": "Google Play"
		},
		"toast": {
			"addEmail": {
				"error": "Failed to add email",
				"errorSendVerification": "Failed to send verification email",
				"loading": "Adding email...",
				"success": "Email added successfully, please verify it"
			},
			"addProducts": {
				"error": "Failed to add products. Please try again.",
				"errorListLimitExceeded": "A list can hold up to {max, number} different products, and this would bring it to {requested, number}. Remove some products or split them across several lists.",
				"inventory": {
					"aggregationSummary": "{count, plural, =1 {1 copy} other {{count} copies}} in {language} ({condition})",
					"successMultiple": "Added {totalCount, plural, =1 {1 product} other {{totalCount} products}} to inventory",
					"successMultipleDetailed": "Added {distinctProductCount, plural, =1 {1 product} other {{distinctProductCount} products}} in {languageCount, plural, =1 {1 language} other {{languageCount} languages}} and {conditionCount, plural, =1 {1 condition} other {{conditionCount} conditions}} to inventory",
					"successSingle": "Added {count, plural, =1 {1 copy} other {{count} copies}} of {productName} to inventory ({condition}, {language})",
					"successSingleGraded": "Added graded {productName} to inventory ({grade} {gradingService}, {language})",
					"successSingleSealed": "Added {count, plural, =1 {1 copy} other {{count} copies}} of {productName} to inventory (Unopened)"
				},
				"list": {
					"aggregationSummary": "{count, plural, =1 {1 copy} other {{count} copies}} in {language} ({condition})",
					"successMultiple": "Added {totalCount, plural, =1 {1 product} other {{totalCount} products}} to list",
					"successMultipleDetailed": "Added {distinctProductCount, plural, =1 {1 product} other {{distinctProductCount} products}} in {languageCount, plural, =1 {1 language} other {{languageCount} languages}} and {conditionCount, plural, =1 {1 condition} other {{conditionCount} conditions}} to list",
					"successSingle": "Added {count, plural, =1 {1 copy} other {{count} copies}} of {productName} to list ({condition}, {language})",
					"successSingleSealed": "Added {count, plural, =1 {1 copy} other {{count} copies}} of {productName} to list (Unopened)"
				},
				"loading": "Adding products...",
				"success": "Successfully added products",
				"successDetails": "{count} {product}, {finish} added in {language} and {condition}"
			},
			"changeFinish": { "error": "Could not change the finish" },
			"changePrintings": { "error": "Could not change the printing" },
			"quantityExceedsInventory": { "error": "Quantity cannot exceed available inventory" },
			"commitImport": {
				"error": "Failed to commit import. Please try again.",
				"loading": "Submitting import...",
				"success": "Import successful"
			},
			"copiedToClipboard": "Copied to clipboard",
			"createImport": {
				"error": "Failed to commit import. Please try again.",
				"loading": "Creating import...",
				"success": "Import created successfully"
			},
			"createList": {
				"error": "Failed to create list. Please try again.",
				"errorListCountLimit": "You already have {max} lists, the maximum allowed. Delete a list to create a new one.",
				"errorMixedGames": "Failed to create list. Please only select products from the same game.",
				"loading": "Creating list...",
				"success": "List created successfully"
			},
			"disconnectAccount": {
				"error": "Failed to disconnect account",
				"loading": "Disconnecting account...",
				"success": "Account disconnected"
			},
			"downloadStarted": "Download started",
			"passwordCreate": {
				"error": "Failed to create password",
				"loading": "Creating password...",
				"success": "Password created successfully"
			},
			"passwordUpdate": {
				"error": "Failed to update password",
				"loading": "Updating password...",
				"success": "Password updated successfully"
			},
			"priceSettingsDialog": {
				"error": "Failed to update prices",
				"loading": "Updating prices...",
				"success": "Prices updated successfully"
			},
			"removeEmail": {
				"error": "Failed to remove email",
				"loading": "Removing email...",
				"success": "Email removed successfully"
			},
			"resendVerificationCode": {
				"error": "Failed to send verification code",
				"loading": "Sending verification code...",
				"success": "Verification code sent"
			},
			"revokeSession": {
				"error": "Failed to sign out device",
				"loading": "Revoking sessions...",
				"success": "Device signed out successfully"
			},
			"revokeSessions": {
				"error": "Failed to sign out all devices",
				"loading": "Revoking sessions...",
				"success": "All other devices signed out"
			},
			"signup": {
				"emailAlreadyExists": "Email already exists",
				"error": "Failed to register. Please try again.",
				"loading": "Registering...",
				"success": "Registered successfully"
			},
			"updateSettings": {
				"error": "Failed to update settings. Please try again.",
				"loading": "Updating settings...",
				"success": "Settings updated successfully"
			},
			"updateUsername": {
				"error": "Failed to update username",
				"errors": {
					"invalidCharacter": "Usernames can only contain letters, numbers, '_' and '-'.",
					"invalidLength": "Your username doesn't meet the length requirements.",
					"needsNonNumber": "Usernames can't contain only numbers."
				},
				"loading": "Updating username...",
				"success": "Username updated successfully"
			},
			"verifyEmail": {
				"error": "Invalid verification code",
				"loading": "Verifying email...",
				"success": "Email verified successfully"
			}
		}
	},
	business: { "chat": { "timestampAt": "at" } },
	orders: { "dispute": {
		"askForHelp": {
			"issueCategory": {
				"label": "What do you need help with?",
				"missingItem": "One or more items are missing from my order",
				"wrongCondition": "One or more items have the wrong condition",
				"counterfeitResealed": "One or more items are counterfeit products",
				"wrongProduct": "One or more items are not the ones I ordered",
				"damaged": "One or more items arrived damaged",
				"notReceived": "My order hasn't arrived",
				"notReceivedTooltip": "Wait until your order is shipped before selecting this option",
				"notReceivedDisabledTooltip": "You must wait 7 days after the parcel has been shipped before you can report that it has not arrived.",
				"notReceivedExclusiveTooltip": "You cannot select 'Not received' when other issues are selected",
				"otherDisabledByNotReceivedTooltip": "You cannot select other issues when 'Not received' is selected",
				"other": "Other"
			},
			"requestedOutcome": {
				"label": "How do you want the seller to resolve this problem?",
				"fullRefund": "Refund my order",
				"partialRefund": "Partially refund my order",
				"noRefund": "No refund needed (just resolve the issue)",
				"returnRefund": "Send me a replacement"
			}
		},
		"chatAttachments": {
			"issueOpened": {
				"youOpened": "You opened an issue",
				"buyerOpened": "Issue opened by the buyer",
				"issueReported": "Issue reported",
				"preferredResolution": "Preferred resolution",
				"message": "Message",
				"attachedFiles": "Attached files",
				"markAsResolved": "Mark issue as resolved",
				"offerRefund": "Offer partial or full refund",
				"evidenceLabel": "Evidence {number}",
				"evidenceLoadFailed": "Evidence {number} could not be loaded"
			},
			"proposalMade": {
				"youReceivedOffer": "You received a {offerType} offer",
				"youSentOffer": "You sent a {offerType} offer",
				"offerAmount": "Offer amount",
				"offer": "Offer",
				"acceptOffer": "Accept offer",
				"declineOffer": "Decline offer"
			},
			"proposalAccepted": {
				"youAccepted": "You accepted the {offerType}",
				"buyerAccepted": "Buyer accepted the {offerType}",
				"amount": "Amount",
				"resolution": "Resolution",
				"resolutionAccepted": "Resolution accepted"
			},
			"proposalDeclined": {
				"youDeclined": "You declined the {offerType}",
				"buyerDeclined": "Buyer declined the {offerType}",
				"declinedOffer": "Declined offer"
			},
			"escalated": {
				"you": "you",
				"escalationAlert": "You can't contact the {counterparty} anymore because {escalator} have a support request ongoing on this order",
				"youEscalated": "You escalated this dispute",
				"buyerEscalated": "Buyer escalated this dispute",
				"sellerEscalated": "Seller escalated this dispute",
				"disputeEscalated": "Dispute was escalated",
				"reasonForEscalation": "Reason for escalation",
				"escalationInfo": "This dispute has been escalated to our support team. We'll review the case and reach out within 24-48 hours."
			},
			"supportRecap": {
				"youSentRequest": "You sent a support request to CardNexus",
				"buyerSentRequest": "The buyer sent a support request to CardNexus",
				"sellerSentRequest": "The seller sent a support request to CardNexus",
				"systemEscalated": "This dispute was automatically escalated to CardNexus support",
				"supportInfo": "Your request for help has been received. Our support team is reviewing it, and the order is paused until the issue is resolved. You'll get a response within {N} business days.",
				"escalationComment": "Your comment"
			},
			"resolved": {
				"title": "Dispute resolved by CardNexus",
				"resolution": "Resolution",
				"outcomeFullRefund": "Full refund",
				"outcomePartialRefund": "Partial refund of {amount}",
				"outcomeNoRefund": "No refund",
				"disputeClosed": "Dispute closed"
			},
			"offerTypes": {
				"fullRefund": "full refund",
				"partialRefund": "partial refund",
				"returnRefund": "return and refund",
				"refund": "refund",
				"fullRefundOffer": "full refund offer",
				"partialRefundOffer": "partial refund offer",
				"returnRefundOffer": "return and refund offer",
				"offer": "offer",
				"noRefund": "no refund",
				"noRefundOffer": "no refund offer"
			},
			"outcomes": {
				"fullRefund": "Full refund",
				"returnRefund": "Return items for refund"
			}
		}
	} },
	mcpWidgets: {
		"common": {
			"openOnCardNexus": "Open on CardNexus",
			"seeListings": "See listings",
			"showMore": "Show more",
			"back": "Back",
			"close": "Close",
			"loading": "Loading",
			"loadingWith": "Loading {summary}",
			"errorTitle": "Something went wrong",
			"rateLimited": "{message} Try again in {seconds, plural, one {# second} other {# seconds}}.",
			"photosOnSite": "All photos are on CardNexus.",
			"moreOnSite": "Some results are only on CardNexus.",
			"imagePlaceholder": "No image",
			"unknownResult": "Nothing to show here.",
			"selectedRange": "Selected",
			"expand": "Expand",
			"collapse": "Collapse",
			"added": "Added"
		},
		"products": {
			"marketPrice": "Market price",
			"lowestPrice": "Lowest CardNexus price",
			"noPrice": "No price yet",
			"listingCount": "{count, plural, one {# listing} other {# listings}}",
			"noResults": "No products found",
			"openSearch": "Open this search on CardNexus",
			"change7d": "{percent} over 7 days",
			"sealed": "Sealed product",
			"finish": "Finish",
			"attributes": "Details"
		},
		"sets": {
			"seeCards": "See cards",
			"cardCount": "{count, plural, one {# card} other {# cards}}",
			"sealedCount": "{count, plural, one {# sealed product} other {# sealed products}}",
			"released": "Released {date}",
			"seeSets": "See sets",
			"noResults": "No sets found",
			"symbolAlt": "{name} symbol"
		},
		"history": {
			"title": "Market price history",
			"rangeLabel": "Time range",
			"range30d": "30 days",
			"range90d": "90 days",
			"range1y": "1 year",
			"regionEu": "Europe",
			"regionNa": "North America",
			"change": "Change over the range: {percent}",
			"latest": "Latest: {price}",
			"lowHigh": "Low {low}, high {high}",
			"empty": "No price history in this range",
			"chartLabel": "Market price history of {name}, {region}, {finish}",
			"seriesLabel": "Series"
		},
		"listings": {
			"addToCart": "Add to cart",
			"adding": "Adding",
			"inCart": "In your cart",
			"openCart": "Open cart",
			"addOnSite": "Add on CardNexus",
			"seeOtherListings": "See other listings",
			"quantityAvailable": "{count} available",
			"belowMarket": "{percent} below market price",
			"aboveMarket": "{percent} above market price",
			"atMarket": "At market price",
			"shipping": "Shipping {price}",
			"ratingLabel": "Rated {average} out of 5 from {count, plural, one {# review} other {# reviews}}",
			"noReviews": "No reviews yet",
			"photos": "{count, plural, one {# photo} other {# photos}}",
			"viewPhotos": "View photos",
			"photoOf": "Photo {index} of {total}",
			"previousPhoto": "Previous photo",
			"nextPhoto": "Next photo",
			"empty": "No listings match these filters",
			"openSearch": "Open this search on CardNexus",
			"graded": "{service} {grade}",
			"sellerPro": "Professional seller",
			"sellerIndividual": "Private seller",
			"memberSince": "Member since {date}",
			"verifiedSales": "{count, plural, one {# verified sale} other {# verified sales}}",
			"notTakingOrders": "Not taking orders right now",
			"completionRate": "{percent} of recent orders completed",
			"shipsIn": "Ships in about {hours, plural, one {# hour} other {# hours}}",
			"sellerListings": "Listings from {seller}",
			"listingOf": "Listing of {name}",
			"sellerProfile": "Seller"
		},
		"cart": {
			"title": "Your cart",
			"itemCount": "{count, plural, one {# item} other {# items}}",
			"empty": "Your cart is empty",
			"items": "Items",
			"shipping": "Shipping",
			"total": "Total",
			"shippingNotQuoted": "Shipping not quoted",
			"reviewAndPay": "Review and pay on CardNexus",
			"removeNamed": "Remove {name}",
			"increase": "Increase quantity of {name}",
			"decrease": "Decrease quantity of {name}",
			"quantity": "Quantity {count}",
			"footnote": "Totals exclude the buyer fee, VAT and promotions. Final prices are shown in your cart on CardNexus.",
			"deliveryTo": "Delivery to {country}",
			"failures": "{count, plural, one {# listing was not added} other {# listings were not added}}",
			"added": "Added to your cart",
			"updating": "Updating your cart",
			"remove": "Remove"
		},
		"plan": {
			"title": "Basket options",
			"modeLowestPrice": "Lowest price",
			"modeFewestSellers": "Fewest sellers",
			"modeBalanced": "Balanced",
			"sellers": "{count, plural, one {# seller} other {# sellers}}",
			"items": "Items",
			"shipping": "Shipping",
			"total": "Total",
			"unmet": "{count, plural, one {# card could not be sourced} other {# cards could not be sourced}}",
			"replaceCart": "Replace my cart with this option",
			"replacing": "Replacing your cart",
			"replaced": "Your cart was replaced.",
			"openCart": "Open cart",
			"openBuyList": "Open the buy list on CardNexus",
			"calculating": "Calculating your options",
			"checkAgain": "Check again",
			"failed": "The plan could not be calculated.",
			"noOptions": "No options found",
			"footnote": "Final prices are shown in your cart on CardNexus."
		}
	}
};

//#endregion
//#region src/i18n/fr.json
var fr_default = {
	global: {
		"addProductMethods": {
			"addGraded": {
				"description": "Ajouter des cartes gradées à votre inventaire en recherchant leur nom",
				"title": "Ajouter une carte gradée"
			},
			"bulkImport": {
				"description": "Créer un import pour télécharger votre inventaire en masse (CSV, édition de texte, etc.)",
				"title": "Import en masse"
			},
			"cardScanner": {
				"description": "Scannez vos cartes avec l'appareil photo de votre téléphone pour les ajouter instantanément. Disponible sur l'application mobile CardNexus.",
				"title": "Scan de cartes"
			},
			"criteriaBuilder": {
				"description": "Trouver et ajouter des groupes entiers de cartes basés sur l'extension, la finition, le type, la couleur, etc.",
				"title": "Ajouter par filtre"
			},
			"csv": {
				"description": "Télécharger un fichier de collection (CSV) depuis un autre service ou tableur.",
				"title": "Importer un fichier"
			},
			"manual": {
				"description": "Explorer le catalogue complet de produits pour <bold>{gameName}</bold>, puis filtrer et sélectionner ceux que vous possédez.",
				"title": "Parcourir le catalogue"
			},
			"preset": {
				"description": "Parfait si vous collectionnez des sets complets, playsets, master sets",
				"title": "Set prédéfini"
			},
			"search": {
				"description": "Ajouter des produits spécifiques un par un en recherchant leur nom",
				"title": "Recherche rapide"
			},
			"text": {
				"description": "Copier et coller un deck ou une liste pour ajouter toutes les cartes instantanément.",
				"title": "Coller une liste"
			}
		},
		"common": {
			"featured": "À la une",
			"myGames": "Mes jeux",
			"otherGames": "Autres jeux",
			"accessDenied": "Accès refusé",
			"account": "Compte",
			"add": "Ajouter",
			"addCards": "Ajouter des produits",
			"addCardsFromCsv": "Ajouter des cartes à partir d'un csv",
			"addCardsFromPhotos": "Ajouter des cartes depuis des photos",
			"addCardsFromPreset": "Ajouter des cartes à partir d'un set prédéfini",
			"addCardsFromText": "Ajouter des cartes à partir de texte",
			"addCardsToInventory": "Ajouter des produits à l'inventaire",
			"addCardsToList": "Ajouter des produits à la liste",
			"addCardsWithCriteriaBuilder": "Ajouter des cartes par filtres",
			"addToCart": "Ajouter au panier",
			"addVariantToInventory": "Ajouter une variante à l'inventaire",
			"addMoreCards": "Ajouter plus de produits",
			"addOneToImport": "Ajouter 1 à l'import",
			"addOneToInventory": "Ajouter 1 à l'inventaire",
			"addOneToList": "Ajouter 1 à la liste",
			"addPicture": "Ajouter une image",
			"addProducts": "Ajouter des produits",
			"addToImport": "Ajouter à l'import",
			"addToInventory": "Ajouter à l'inventaire",
			"addToList": "Ajouter à la liste",
			"advancedSettings": "Paramètres avancés",
			"allRightsReserved": "Tous droits réservés",
			"and": "et",
			"apply": "Appliquer",
			"asc": "Croissant",
			"availability": "Disponibilité",
			"back": "Retour",
			"backToExplore": "Retour à l'exploration",
			"backToHomepage": "Retour à l'accueil",
			"backToImport": "Retour à l'import",
			"backToInventory": "Retour à l'inventaire",
			"backToList": "Retour à la liste",
			"backToTop": "Retour en haut",
			"backToSearch": "Retour à la recherche",
			"binder": "Classeur",
			"blue": "Bleu",
			"box": "Boîte",
			"browse": "Parcourir",
			"buyer": "Acheteur",
			"cancel": "Annuler",
			"cards": "{count, plural, =0 {cartes} =1 {carte} other {cartes}}",
			"cardCount": "{count, plural, =0 {Aucune carte} =1 {1 carte} other {# cartes}}",
			"sealedCount": "{count, plural, =0 {Aucun scellé} =1 {1 scellé} other {# scellés}}",
			"cardsInAttribute": "{count, plural, =1 {# carte dans {attribute}} other {# cartes dans {attribute}}}",
			"cardsSelected": "{count, plural, =0 {Aucune carte sélectionnée} =1 {Carte sélectionnée} other {Cartes sélectionnées}}",
			"certification": "Certification",
			"clear": "Effacer",
			"clearSearch": "Effacer la recherche",
			"close": "Fermer",
			"collection": "Collection",
			"colors": "Couleurs",
			"columns": "Colonnes",
			"columnName": "Nom",
			"columnShown": "Affichée",
			"showHideColumns": "Colonnes affichées",
			"pinColumns": "Épingler les colonnes",
			"pinLeft": "Épingler à gauche",
			"pinRight": "Épingler à droite",
			"resetColumns": "Réinitialiser les colonnes",
			"comingSoon": "Bientôt disponible",
			"conditions": "{count, plural, =1 {condition} other {conditions}}",
			"connectAccount": "Lier le compte",
			"confirm": "Confirmer",
			"containerName": "Nom du conteneur",
			"continue": "Continuer",
			"copies": "{count, plural, =0 {# exemplaires} =1 {# exemplaire} other {# exemplaires}}",
			"copy": "Copier",
			"copyLink": "Copier le lien",
			"copyToClipboard": "Copier dans le presse-papiers",
			"copying": "Copie en cours",
			"couldntLoadLocations": "Impossible de charger les emplacements",
			"create": "Créer",
			"createList": "Créer une liste",
			"createNewContainer": "Créer un nouveau conteneur",
			"createdOn": "Créé le",
			"criteriaBuilder": "Générateur de critères",
			"csvImport": "Import CSV",
			"currencyWarning": "Les prix proviennent de {priceSource} en {priceSourceCurrency}, mais sont affichés en {displayCurrency}. Les taux de change peuvent affecter la précision.",
			"current": "Actuel",
			"date": "Date",
			"deck": "Deck",
			"default": "Défaut",
			"delete": "Supprimer",
			"deleteAll": "Tout supprimer",
			"desc": "Décroissant",
			"description": "Description",
			"details": "Détails",
			"differentAttributes": "{count, plural, =1 {# {attribute} pour cette carte} other {# {attribute}s différents pour cette carte}}",
			"differentCopies": "{count, plural, =0 {Aucun exemplaire différent} =1 {1 exemplaire différent} other {# exemplaires différents}}",
			"differentFinishes": "{count, plural, =0 {Aucune finition différente} =1 {1 finition} other {# finitions différentes}}",
			"disconnect": "Déconnecter",
			"collect": "Collection",
			"download": "Télécharger",
			"downloadAsCsv": "Télécharger en CSV",
			"downloadAsJson": "Télécharger en JSON",
			"display": "Afficher",
			"dontSave": "Ne pas enregistrer",
			"dragToReorder": "Glisser pour réorganiser",
			"edit": "Modifier",
			"equalTo": "Égal à",
			"error": "Erreur",
			"errorDescription": "Une erreur s'est produite. Veuillez réessayer ou contacter notre support si le problème persiste.",
			"export": "Exporter",
			"exportAsCsv": "Exporter en CSV",
			"exportAsCsvOrJson": "Exporter en CSV/JSON",
			"exportAsList": "Exporter comme liste",
			"exportData": "Exporter les données",
			"exportingData": "Exportation des données",
			"exportCompleted": "Exportation terminée avec succès",
			"exportFailed": "Échec de l'exportation",
			"exportPreviewFailed": "Échec de la prévisualisation de l'exportation",
			"green": "Vert",
			"icon": "Icône",
			"locationName": "Nom de l'emplacement",
			"noLocationsFound": "Aucun emplacement trouvé",
			"pink": "Rose",
			"processingExport": "Traitement de l'exportation...",
			"preparingDownload": "Préparation du téléchargement...",
			"generating": "Génération",
			"gallery": "Galerie",
			"expired": "Expiré",
			"finishes": "{count, plural, =0 {Aucune finition} =1 {1 finition} other {# finitions}}",
			"filters": "Filtres",
			"for": "pour",
			"forSale": "À vendre",
			"forSaleWithPrice": "À vendre · {sellPrice}",
			"games": "{count, plural, =0 {jeu} =1 {jeu} other {jeux}}",
			"goToHomepage": "Aller à l'accueil",
			"goToTheApp": "Aller à l'application",
			"grade": "Grade",
			"grader": "Gradeur",
			"greaterThan": "Plus grand que",
			"grid": "Grille",
			"group": "Groupe",
			"groupBy": "Grouper par",
			"hideOutOfStock": "En stock uniquement",
			"high": "Haut",
			"hold": "À conserver",
			"hotDeals": "Bons plans",
			"red": "Rouge",
			"searchLocations": "Rechercher des emplacements",
			"selectLocationOrCreate": "Sélectionnez un emplacement ou créez-en un nouveau",
			"selectAllTagsOrCreate": "Sélectionnez des tags ou créez-en un",
			"createCustomTag": "Créer un tag personnalisé",
			"tagName": "Nom du tag",
			"defaultTags": {
				"onLoan": "Prêté",
				"investment": "Investissement",
				"treasure": "Trésor",
				"forTrade": "À échanger",
				"toGrade": "À grader",
				"toList": "À mettre en vente"
			},
			"todayHotDeals": "Bons plans du jour",
			"seeAllHotDeals": "Voir tous les bons plans",
			"explorePerGame": "Explorer par jeu",
			"identifier": "Identifiant",
			"in": "Dans",
			"invalid": "Invalide",
			"itemsPerPage": "Éléments par page",
			"languages": "{count, plural, =1 {langue} other {langues}}",
			"lastUpdated": "Dernière mise à jour",
			"lessThan": "Plus petit que",
			"line": "Ligne",
			"list": "Liste",
			"listings": "Annonces",
			"listForSale": "Mettre en vente",
			"addForSale": "Ajouter à la vente",
			"cancelListing": "Supprimer l'annonce",
			"putForSale": "Mettre en vente",
			"individual": "Particulier",
			"proSellerBadge": "Pro",
			"professional": "Professionnel",
			"notGraded": "Non gradée",
			"graded": "Gradée",
			"lists": "Listes",
			"loading": "Chargement",
			"low": "Bas",
			"manageAccount": "Gérer le compte",
			"manualSearch": "Recherche manuelle",
			"manualSelection": "Sélection manuelle",
			"marketOverview": "Aperçu du marché",
			"marketValue": "Valeur de marché",
			"fairPrice": "Prix juste",
			"fairPriceTooltip": "Calculé à partir de plusieurs sources de données, en tenant compte de l'état, de la langue et de la finition de la carte.",
			"sellValue": "Valeur de vente",
			"buy": "Acheter",
			"justListed": "Vient d'arriver",
			"hotPrice": "Prix bas",
			"sortNewest": "Plus récents",
			"sortPrice": "Prix",
			"sortGrade": "Grade",
			"marketplaceWarning": "Notre équipe travaille à ajouter les données de prix {marketplace} pour {game}",
			"max": "Max",
			"maxQuantityInCart": "Quantité maximale déjà ajoutée au panier",
			"regionMismatch": "Ce vendeur est dans une région différente des articles de votre panier",
			"regionMismatchAlert": "<bold>Vous avez des articles de vendeurs {region} dans votre panier.</bold> Les annonces de l'autre région sont désactivées. <checkoutLink>Finalisez d'abord votre commande</checkoutLink> pour ajouter des articles d'une autre région.",
			"mid": "Moyen",
			"min": "Min",
			"myCollection": "Ma collection",
			"marketActivity": "Activité du marché",
			"next": "Suivant",
			"no": "Non",
			"noDataAvailable": "Aucune donnée disponible",
			"noMoreResults": "Plus de résultats",
			"noOptionsAvailable": "Aucune option disponible",
			"noOptionsDescription": "Il n'y a aucune option à sélectionner",
			"noResultDescription": "Vous pouvez réinitialiser vos filtres en cliquant ci-dessous.",
			"noProductsFound": "Aucun produit trouvé",
			"noResults": "Aucun résultat",
			"noResultsDescription": "Essayez d'ajuster vos termes de recherche",
			"none": "Aucun",
			"notAvailable": "Non disponible",
			"notAvailableShort": "N/D",
			"of": "de",
			"off": "Désactivé",
			"listingsFrom": "{count, plural, =1 {1 annonce à partir de} other {# annonces à partir de}}",
			"on": "Activé",
			"oops": "Oups, quelque chose s'est mal passé",
			"otherPrintings": "{count, plural, =0 {Aucune autre version} =1 {# autre version} other {# autres versions}}",
			"outOfStock": "Rupture de stock",
			"pageNotFound": "Page non trouvée",
			"pendingUpdates": "{count, plural, =0 {Aucune mise à jour en attente} =1 {1 mise à jour en attente} other {# mises à jour en attente}}",
			"pictures": "Photos",
			"points": "{count, plural, =0 {Aucun point} =1 {1 point} other {# points}}",
			"preferences": "Préférences",
			"previous": "Précédent",
			"price": "Prix",
			"primary": "Principal",
			"printNumber": "Numéro de collection",
			"printing": "Version",
			"private": "Privé",
			"product": "Produit",
			"products": "{count, plural, =0 {produit} =1 {produit} other {produits}}",
			"productCount": "{count, plural, =0 {Aucun produit} =1 {1 produit} other {# produits}}",
			"productDetails": "Détails du produit",
			"priceLabel": "Prix",
			"profile": "Profil",
			"public": "Public",
			"quantity": "Quantité",
			"referrals": "Parrainages",
			"releasedOn": "Sorti le {date}",
			"remove": "Retirer",
			"refineFilters": "Affiner les filtres",
			"resendCode": "Renvoyer le code",
			"reset": "Réinitialiser",
			"resetFilters": "Réinitialiser les filtres",
			"resetToDefaults": "Réinitialiser",
			"results": "{count, plural, =0 {résultat} =1 {résultat} other {résultats}}",
			"resultsCount": "{count, plural, =0 {0 résultat} =1 {1 résultat} other {{count} résultats}}",
			"searchDepthNoticeDescription": "Affinez votre recherche avec les filtres pour voir le reste.",
			"searchDepthNoticeLastPageTitle": "Ceci est la dernière page consultable",
			"retry": "Réessayer",
			"reviews": "{count, plural, =0 {Aucun avis} =1 {1 avis} other {# avis}}",
			"save": "Enregistrer",
			"saved": "Enregistré",
			"saving": "Enregistrement",
			"search": "Rechercher une valeur",
			"seller": "Vendeur",
			"selectColumns": "Sélectionner les colonnes",
			"select": "Sélectionner",
			"selectAll": "Tout sélectionner",
			"deselectAll": "Tout désélectionner",
			"commonProperties": "Propriétés communes",
			"availableForAllGames": "Disponible pour tous les jeux",
			"gameSpecificFields": "Propriétés spécifiques à {game}",
			"seeAll": "Voir tout",
			"seeAllPrintings": "Voir toutes les versions",
			"seeAllSealedProducts": "Voir tous les produits scellés",
			"seeMoreResults": "Voir plus de résultats",
			"selectOptions": "Sélectionner des options",
			"sellPrice": "Prix de vente",
			"sellPriceTooltip": "Le prix de vente par carte individuelle dans cette liste",
			"settings": "Paramètres",
			"moreActions": "Plus d'actions",
			"shareList": "Partager la liste",
			"show": "Afficher",
			"showLess": "Voir moins",
			"showMore": "Voir {count} de plus",
			"showing": "Affichage",
			"signIn": "Se connecter",
			"signInShort": "Connexion",
			"signOut": "Se déconnecter",
			"signUp": "S'inscrire",
			"sort": "Trier",
			"sortBy": "Trier par",
			"sortDirection": "Direction du tri",
			"sortOrder": "Ordre de tri",
			"status": "Statut",
			"termsAndConditions": "Conditions générales",
			"textImport": "Import de texte",
			"toComplete": "À compléter",
			"totalValue": "Valeur totale",
			"unknown": "Inconnu",
			"update": "Mettre à jour",
			"uploadPicture": "Télécharger une image",
			"unverified": "Non vérifié",
			"user": "Utilisateur",
			"valid": "Valide",
			"validate": "Valider",
			"validating": "Validation",
			"value": "Valeur",
			"verify": "Vérifier",
			"viewAll": "Voir tout",
			"viewAction": "Voir",
			"viewAs": "Afficher comme",
			"view": "Vue",
			"viewDetails": "Voir les détails",
			"viewInventory": "Voir l'inventaire",
			"viewList": "Voir la liste",
			"visibility": "Visibilité",
			"wantPrice": "Prix recherché",
			"wantPriceTooltip": "Le prix que vous souhaitez payer par carte individuelle dans cette liste",
			"white": "Blanc",
			"withBulkImport": "Import en masse",
			"withCSVImport": "Importer depuis un fichier",
			"withCriteriaBuilder": "Ajouter par filtre",
			"withPreset": "Utiliser un set prédéfini",
			"withQuickSearch": "Recherche rapide",
			"withTextEdit": "Coller une liste",
			"yellow": "Jaune",
			"zoomLevel": "Niveau de zoom",
			"zoomLevelHint": "Taille des cartes en vue grille et galerie",
			"yes": "Oui",
			"you": "Vous",
			"zoomedIn": "Zoom avant",
			"zoomedOut": "Zoom arrière",
			"required": "Requis",
			"noImage": "Aucune image",
			"new": "New"
		},
		"productTypes": {
			"all": "Tous les produits",
			"sealed": "{count, plural, =0 {Aucun produit scellé} =1 {Produit scellé} other {Produits scellés}}",
			"card": "{count, plural, =0 {Aucune carte à l'unité} =1 {Carte à l'unité} other {Cartes à l'unité}}"
		},
		"countries": {
			"us": "États-Unis",
			"gb": "Royaume-Uni",
			"ca": "Canada",
			"au": "Australie",
			"nz": "Nouvelle-Zélande",
			"hk": "Hong Kong",
			"eu": "Union Européenne",
			"at": "Autriche",
			"be": "Belgique",
			"cy": "Chypre",
			"ee": "Estonie",
			"fi": "Finlande",
			"fr": "France",
			"de": "Allemagne",
			"gr": "Grèce",
			"hr": "Croatie",
			"hu": "Hongrie",
			"ie": "Irlande",
			"it": "Italie",
			"lv": "Lettonie",
			"lt": "Lituanie",
			"lu": "Luxembourg",
			"mt": "Malte",
			"nl": "Pays-Bas",
			"pt": "Portugal",
			"sk": "Slovaquie",
			"si": "Slovénie",
			"es": "Espagne",
			"ch": "Suisse",
			"pl": "Pologne",
			"se": "Suède",
			"dk": "Danemark",
			"no": "Norvège"
		},
		"errors": {
			"confirmPassword": "Les mots de passe doivent correspondre",
			"copy": "Échec de la copie dans le presse-papiers",
			"emailInvalid": "Veuillez entrer une adresse email valide",
			"fileUploadFailed": "Échec du téléchargement du fichier. Veuillez réessayer.",
			"forbidden": "Accès non autorisé",
			"forbiddenTitle": "Accès interdit",
			"maintenance": "Le service est actuellement en maintenance. Veuillez réessayer plus tard.",
			"maintenanceTitle": "Mode maintenance",
			"maxChars": "Veuillez saisir au maximum {count} caractères",
			"maxValue": "Veuillez saisir une valeur inférieure ou égale à {max}",
			"minChars": "Veuillez saisir au moins {count} caractères",
			"minValue": "Veuillez saisir une valeur supérieure ou égale à {min}",
			"mustSelectLocation": "Vous devez sélectionner un lieu dans la liste déroulante",
			"network": "Une erreur réseau s'est produite. Veuillez réessayer plus tard.",
			"networkTitle": "Erreur réseau",
			"notFound": "La ressource que vous recherchez n'existe pas",
			"notFoundTitle": "Page introuvable",
			"password": "Le mot de passe doit comporter au moins 8 caractères",
			"rateLimit": "Vous avez fait trop de requêtes. Veuillez réessayer plus tard.",
			"rateLimitTitle": "Limite de requêtes dépassée",
			"rateLimitWithTime": "Vous avez fait trop de requêtes. Veuillez réessayer plus tard. Vous pouvez réessayer dans {seconds} secondes.",
			"required": "Ce champ est obligatoire",
			"resetMyAccountConfirmation": "Écrivez 'reset my account' pour confirmer",
			"selectAtLeast": "Veuillez sélectionner au moins {count} options",
			"selectAtLeastOne": "Veuillez sélectionner au moins une option",
			"server": "Une erreur s'est produite sur le serveur. Veuillez réessayer plus tard.",
			"serverTitle": "Erreur serveur",
			"timeout": "La requête a pris trop de temps. Veuillez réessayer.",
			"timeoutTitle": "Délai d'attente dépassé",
			"unauthorized": "Accès non autorisé",
			"unauthorizedTitle": "Non autorisé",
			"unknown": "Une erreur inconnue s'est produite. Veuillez réessayer plus tard.",
			"unknownTitle": "Erreur inconnue",
			"validation": "Votre saisie est invalide",
			"validationTitle": "Erreur de validation",
			"copyForSupport": "Copier l'erreur pour le support",
			"certificationRequired": "Le numéro de certification est requis pour les cartes gradées",
			"copied": "Copié !"
		},
		"labels": {
			"alreadyInList": "Déjà dans la liste",
			"city": "Ville",
			"confirmPassword": "Confirmer le mot de passe",
			"currency": "Devise",
			"currentPassword": "Mot de passe actuel",
			"description": "Description",
			"email": "Adresse email",
			"language": "Langue",
			"layout": "Mise en page",
			"location": "Emplacement",
			"marketplace": "Source de prix",
			"name": "Nom",
			"newPassword": "Nouveau mot de passe",
			"password": "Mot de passe",
			"presetType": "Type de set prédéfini",
			"profile": "Profil",
			"signOutOfOtherSessions": "Se déconnecter de tous les autres appareils après le changement de mot de passe",
			"username": "Nom d'utilisateur",
			"value": "Valeur",
			"amount": "Montant"
		},
		"locale": {
			"description": "La langue de la plateforme",
			"name": "Langue d'affichage",
			"placeholder": "Choisissez la langue de la plateforme",
			"values": {
				"de": "Allemand",
				"en": "Anglais",
				"es": "Espagnol",
				"fr": "Français",
				"it": "Italien"
			}
		},
		"maintenance": {
			"title": "Nous sommes actuellement en maintenance",
			"messageFromTeam": "Message de l'équipe",
			"startsAt": "Commence à",
			"endsAt": "Fin prévue",
			"autoRefresh": "Cette page se rafraîchira automatiquement lorsque la maintenance sera terminée.",
			"banner": { "message": "CardNexus sera en maintenance le <time>{datetime}</time> pour <strong>{duration}</strong>" }
		},
		"siteAlert": { "dismiss": "Fermer" },
		"clientPass": {
			"title": "Une vérification rapide avant de continuer",
			"description": "CardNexus doit confirmer qu'une personne utilise ce navigateur. Effectuez la vérification ci-dessous : la page reprend dès qu'elle est validée."
		},
		"periods": {
			"allTime": "Toutes périodes",
			"oneWeek": "1 semaine",
			"oneYear": "1 an",
			"sixMonths": "6 mois",
			"thirtyDays": "30 jours",
			"threeMonths": "3 mois"
		},
		"marketplaceOptions": {
			"Cardmarket": "Cardmarket",
			"TCGplayer": "TCGplayer"
		},
		"placeholders": {
			"allGames": "Tous les jeux",
			"confirmPassword": "Confirmer le nouveau mot de passe",
			"currency": "Sélectionnez votre devise préférée",
			"currentPassword": "Entrer le mot de passe actuel",
			"email": "Ex : abc@xyz.com",
			"enterValue": "Entrer une valeur",
			"location": "Rechercher une ville...",
			"marketplace": "Sélectionner une source de prix",
			"newPassword": "Entrer le nouveau mot de passe",
			"password": "Entrer le mot de passe",
			"search": "Rechercher",
			"searchUsernameOrOrder": "Rechercher un utilisateur ou numéro de commande",
			"selectExpansion": "Sélectionner une extension",
			"selectGame": "Sélectionner un jeu",
			"selectGroupBy": "Sélectionner grouper par option",
			"selectStatus": "Sélectionner un statut",
			"username": "Ex : jacethemindsculptor"
		},
		"sortValues": {
			"bestMatch": "Meilleure correspondance",
			"cardCount": "Nombre de cartes",
			"sealedProductCount": "Nombre de produits scellés",
			"code": "Code d'extension",
			"completion": "Complétion",
			"createdAt": "Créé le",
			"releaseDate": "Date de sortie",
			"updatedAt": "Mis à jour le"
		},
		"storeBadge": {
			"downloadOn": "Télécharger sur",
			"appStore": "App Store",
			"getItOn": "Disponible sur",
			"googlePlay": "Google Play"
		},
		"toast": {
			"addEmail": {
				"error": "Échec de l'ajout de l'email",
				"errorSendVerification": "Échec de l'envoi de l'email de vérification",
				"loading": "Ajout de l'email...",
				"success": "Email ajouté avec succès, veuillez le vérifier"
			},
			"addProducts": {
				"error": "Échec de l'ajout des produits. Veuillez réessayer.",
				"errorListLimitExceeded": "Une liste peut contenir jusqu'à {max, number} produits différents, et cet ajout la porterait à {requested, number}. Retirez des produits ou répartissez-les sur plusieurs listes.",
				"inventory": {
					"aggregationSummary": "{count, plural, =1 {1 exemplaire} other {{count} exemplaires}} en {language} ({condition})",
					"successMultiple": "Ajouté {totalCount, plural, =1 {1 produit} other {{totalCount} produits}} à l'inventaire",
					"successMultipleDetailed": "Ajouté {distinctProductCount, plural, =1 {1 produit} other {{distinctProductCount} produits}} en {languageCount, plural, =1 {1 langue} other {{languageCount} langues}} et {conditionCount, plural, =1 {1 condition} other {{conditionCount} conditions}} à l'inventaire",
					"successSingle": "Ajouté {count, plural, =1 {1 exemplaire} other {{count} exemplaires}} de {productName} à l'inventaire ({condition}, {language})",
					"successSingleGraded": "Ajouté {productName} gradé à l'inventaire ({grade} {gradingService}, {language})",
					"successSingleSealed": "Ajouté {count, plural, =1 {1 exemplaire} other {{count} exemplaires}} de {productName} à l'inventaire (Unopened)"
				},
				"list": {
					"aggregationSummary": "{count, plural, =1 {1 exemplaire} other {{count} exemplaires}} en {language} ({condition})",
					"successMultiple": "Ajouté {totalCount, plural, =1 {1 produit} other {{totalCount} produits}} à la liste",
					"successMultipleDetailed": "Ajouté {distinctProductCount, plural, =1 {1 produit} other {{distinctProductCount} produits}} en {languageCount, plural, =1 {1 langue} other {{languageCount} langues}} et {conditionCount, plural, =1 {1 condition} other {{conditionCount} conditions}} à la liste",
					"successSingle": "Ajouté {count, plural, =1 {1 exemplaire} other {{count} exemplaires}} de {productName} à la liste ({condition}, {language})",
					"successSingleSealed": "Ajouté {count, plural, =1 {1 exemplaire} other {{count} exemplaires}} de {productName} à la liste (Unopened)"
				},
				"loading": "Ajout des produits...",
				"success": "Produits ajoutés avec succès",
				"successDetails": "{count} {product}, {finish} ajouté en {language} et {condition}"
			},
			"changeFinish": { "error": "Impossible de changer la finition" },
			"changePrintings": { "error": "Impossible de changer la version" },
			"quantityExceedsInventory": { "error": "La quantité ne peut pas dépasser l'inventaire disponible" },
			"commitImport": {
				"error": "Échec de la validation de l'import. Veuillez réessayer.",
				"loading": "Soumission de l'import...",
				"success": "Import réussi"
			},
			"createImport": {
				"error": "Échec de la validation de l'import. Veuillez réessayer.",
				"loading": "Création de l'import...",
				"success": "Import créé avec succès"
			},
			"createList": {
				"error": "Échec de la création de la liste. Veuillez réessayer.",
				"errorListCountLimit": "Vous avez déjà {max} listes, le maximum autorisé. Supprimez une liste pour en créer une nouvelle.",
				"errorMixedGames": "La création de la liste a échoué. Veuillez ne sélectionner que des produits du même jeu.",
				"loading": "Création de la liste...",
				"success": "Liste créée avec succès"
			},
			"disconnectAccount": {
				"error": "Échec de la déconnexion du compte",
				"loading": "Déconnexion du compte...",
				"success": "Compte déconnecté"
			},
			"passwordCreate": {
				"error": "Échec de la création du mot de passe",
				"loading": "Création du mot de passe...",
				"success": "Mot de passe créé avec succès"
			},
			"passwordUpdate": {
				"error": "Échec de la mise à jour du mot de passe",
				"loading": "Mise à jour du mot de passe...",
				"success": "Mot de passe mis à jour avec succès"
			},
			"priceSettingsDialog": {
				"error": "Échec de la mise à jour des prix",
				"loading": "Mise à jour des prix...",
				"success": "Prix mis à jour avec succès"
			},
			"removeEmail": {
				"error": "Échec de la suppression de l'email",
				"loading": "Suppression de l'email...",
				"success": "Email supprimé avec succès"
			},
			"resendVerificationCode": {
				"error": "Échec de l'envoi du code de vérification",
				"loading": "Envoi du code de vérification...",
				"success": "Code de vérification envoyé"
			},
			"revokeSession": {
				"error": "Échec de la déconnexion de l'appareil",
				"loading": "Révocation des sessions...",
				"success": "Appareil déconnecté avec succès"
			},
			"revokeSessions": {
				"error": "Échec de la déconnexion de tous les appareils",
				"loading": "Révocation des sessions...",
				"success": "Tous les autres appareils sont déconnectés"
			},
			"signup": {
				"emailAlreadyExists": "L'email existe déjà",
				"error": "Échec de l'inscription. Veuillez réessayer.",
				"loading": "Inscription...",
				"success": "Inscription réussie"
			},
			"updateSettings": {
				"error": "Échec de la mise à jour des paramètres. Veuillez réessayer.",
				"loading": "Mise à jour des paramètres...",
				"success": "Paramètres mis à jour avec succès"
			},
			"updateUsername": {
				"error": "Échec de la mise à jour du nom d'utilisateur",
				"errors": {
					"invalidCharacter": "Le nom d'utilisateur ne peut contenir que des lettres, des chiffres, « _ » et « - ».",
					"invalidLength": "Votre nom d'utilisateur ne respecte pas la longueur requise.",
					"needsNonNumber": "Le nom d'utilisateur ne peut pas contenir uniquement des chiffres."
				},
				"loading": "Mise à jour du nom d'utilisateur...",
				"success": "Nom d'utilisateur mis à jour avec succès"
			},
			"verifyEmail": {
				"error": "Code de vérification invalide",
				"loading": "Vérification de l'email...",
				"success": "Email vérifié avec succès"
			},
			"copiedToClipboard": "Copié dans le presse-papiers",
			"downloadStarted": "Téléchargement démarré"
		}
	},
	business: { "chat": { "timestampAt": "à" } },
	orders: { "dispute": {
		"askForHelp": {
			"issueCategory": {
				"label": "De quoi avez-vous besoin ?",
				"missingItem": "Un ou plusieurs articles manquent dans ma commande",
				"wrongCondition": "Un ou plusieurs articles ont un état incorrect",
				"counterfeitResealed": "Un ou plusieurs articles sont des contrefaçons",
				"wrongProduct": "Un ou plusieurs articles ne sont pas ceux que j'ai commandés",
				"damaged": "Un ou plusieurs articles sont arrivés endommagés",
				"notReceived": "Ma commande n'est pas arrivée",
				"notReceivedTooltip": "Attendez que votre commande soit expédiée avant de sélectionner cette option",
				"notReceivedDisabledTooltip": "Vous devez attendre 7 jours après l'expédition du colis avant de pouvoir signaler qu'il n'est pas arrivé.",
				"notReceivedExclusiveTooltip": "Vous ne pouvez pas sélectionner 'Non reçu' lorsque d'autres problèmes sont sélectionnés",
				"otherDisabledByNotReceivedTooltip": "Vous ne pouvez pas sélectionner d'autres problèmes lorsque 'Non reçu' est sélectionné",
				"other": "Autre"
			},
			"requestedOutcome": {
				"label": "Comment souhaitez-vous que le vendeur résolve ce problème ?",
				"fullRefund": "Rembourser ma commande",
				"partialRefund": "Remboursement partiel de ma commande",
				"noRefund": "Pas de remboursement nécessaire (juste résoudre le problème)",
				"returnRefund": "M'envoyer un remplacement"
			}
		},
		"chatAttachments": {
			"issueOpened": {
				"youOpened": "Vous avez ouvert un problème",
				"buyerOpened": "Problème ouvert par l'acheteur",
				"issueReported": "Problème signalé",
				"preferredResolution": "Résolution souhaitée",
				"message": "Message",
				"attachedFiles": "Fichiers joints",
				"markAsResolved": "Marquer comme résolu",
				"offerRefund": "Proposer un remboursement",
				"evidenceLabel": "Pièce justificative {number}",
				"evidenceLoadFailed": "Impossible de charger la pièce justificative {number}"
			},
			"proposalMade": {
				"youReceivedOffer": "Vous avez reçu une offre de {offerType}",
				"youSentOffer": "Vous avez envoyé une offre de {offerType}",
				"offerAmount": "Montant de l'offre",
				"offer": "Offre",
				"acceptOffer": "Accepter l'offre",
				"declineOffer": "Refuser l'offre"
			},
			"proposalAccepted": {
				"youAccepted": "Vous avez accepté {offerType}",
				"buyerAccepted": "L'acheteur a accepté {offerType}",
				"amount": "Montant",
				"resolution": "Résolution",
				"resolutionAccepted": "Résolution acceptée"
			},
			"proposalDeclined": {
				"youDeclined": "Vous avez refusé {offerType}",
				"buyerDeclined": "L'acheteur a refusé {offerType}",
				"declinedOffer": "Offre refusée"
			},
			"escalated": {
				"you": "vous",
				"escalationAlert": "Vous ne pouvez plus contacter {counterparty} car {escalator} avez une demande de support en cours sur cette commande",
				"youEscalated": "Vous avez transféré ce litige",
				"buyerEscalated": "L'acheteur a transféré ce litige",
				"sellerEscalated": "Le vendeur a transféré ce litige",
				"disputeEscalated": "Litige transféré",
				"reasonForEscalation": "Motif du transfert",
				"escalationInfo": "Ce litige a été transmis à notre équipe support. Nous examinerons le dossier et vous contacterons sous 24 à 48 heures."
			},
			"supportRecap": {
				"youSentRequest": "Vous avez envoyé une demande de support à CardNexus",
				"buyerSentRequest": "L'acheteur a envoyé une demande de support à CardNexus",
				"sellerSentRequest": "Le vendeur a envoyé une demande de support à CardNexus",
				"systemEscalated": "Ce litige a été automatiquement transmis au support CardNexus",
				"supportInfo": "Votre demande d'aide a bien été reçue. Notre équipe support l'examine, et la commande est suspendue jusqu'à la résolution du problème. Vous recevrez une réponse sous {N} jours ouvrés.",
				"escalationComment": "Votre commentaire"
			},
			"resolved": {
				"title": "Litige résolu par CardNexus",
				"resolution": "Résolution",
				"outcomeFullRefund": "Remboursement complet",
				"outcomePartialRefund": "Remboursement partiel de {amount}",
				"outcomeNoRefund": "Pas de remboursement",
				"disputeClosed": "Litige clos"
			},
			"offerTypes": {
				"fullRefund": "remboursement complet",
				"partialRefund": "remboursement partiel",
				"returnRefund": "retour et remboursement",
				"refund": "remboursement",
				"fullRefundOffer": "l'offre de remboursement complet",
				"partialRefundOffer": "l'offre de remboursement partiel",
				"returnRefundOffer": "l'offre de retour et remboursement",
				"offer": "l'offre",
				"noRefund": "pas de remboursement",
				"noRefundOffer": "l'offre sans remboursement"
			},
			"outcomes": {
				"fullRefund": "Remboursement complet",
				"returnRefund": "Retour et remboursement"
			}
		}
	} },
	mcpWidgets: {
		"common": {
			"openOnCardNexus": "Ouvrir sur CardNexus",
			"seeListings": "Voir les annonces",
			"showMore": "Afficher plus",
			"back": "Retour",
			"close": "Fermer",
			"loading": "Chargement",
			"loadingWith": "Chargement de {summary}",
			"errorTitle": "Un problème est survenu",
			"rateLimited": "{message} Réessayez dans {seconds, plural, one {# seconde} other {# secondes}}.",
			"photosOnSite": "Toutes les photos sont sur CardNexus.",
			"moreOnSite": "Certains résultats ne sont visibles que sur CardNexus.",
			"imagePlaceholder": "Pas d’image",
			"unknownResult": "Rien à afficher ici.",
			"selectedRange": "Sélectionné",
			"expand": "Agrandir",
			"collapse": "Réduire",
			"added": "Ajouté"
		},
		"products": {
			"marketPrice": "Prix du marché",
			"lowestPrice": "Prix le plus bas sur CardNexus",
			"noPrice": "Pas encore de prix",
			"listingCount": "{count, plural, one {# annonce} other {# annonces}}",
			"noResults": "Aucun produit trouvé",
			"openSearch": "Ouvrir cette recherche sur CardNexus",
			"change7d": "{percent} sur 7 jours",
			"sealed": "Produit scellé",
			"finish": "Finition",
			"attributes": "Détails"
		},
		"sets": {
			"seeCards": "Voir les cartes",
			"cardCount": "{count, plural, one {# carte} other {# cartes}}",
			"sealedCount": "{count, plural, one {# produit scellé} other {# produits scellés}}",
			"released": "Sortie le {date}",
			"seeSets": "Voir les extensions",
			"noResults": "Aucune extension trouvée",
			"symbolAlt": "Symbole de {name}"
		},
		"history": {
			"title": "Historique du prix du marché",
			"rangeLabel": "Période",
			"range30d": "30 jours",
			"range90d": "90 jours",
			"range1y": "1 an",
			"regionEu": "Europe",
			"regionNa": "Amérique du Nord",
			"change": "Variation sur la période : {percent}",
			"latest": "Dernier prix : {price}",
			"lowHigh": "Plus bas {low}, plus haut {high}",
			"empty": "Aucun historique de prix sur cette période",
			"chartLabel": "Historique du prix du marché de {name}, {region}, {finish}",
			"seriesLabel": "Série"
		},
		"listings": {
			"addToCart": "Ajouter au panier",
			"adding": "Ajout en cours",
			"inCart": "Dans votre panier",
			"openCart": "Ouvrir le panier",
			"addOnSite": "Ajouter sur CardNexus",
			"seeOtherListings": "Voir d’autres annonces",
			"quantityAvailable": "{count, plural, one {# disponible} other {# disponibles}}",
			"belowMarket": "{percent} sous le prix du marché",
			"aboveMarket": "{percent} au-dessus du prix du marché",
			"atMarket": "Au prix du marché",
			"shipping": "Livraison {price}",
			"ratingLabel": "Noté {average} sur 5 d’après {count, plural, one {# avis} other {# avis}}",
			"noReviews": "Pas encore d’avis",
			"photos": "{count, plural, one {# photo} other {# photos}}",
			"viewPhotos": "Voir les photos",
			"photoOf": "Photo {index} sur {total}",
			"previousPhoto": "Photo précédente",
			"nextPhoto": "Photo suivante",
			"empty": "Aucune annonce ne correspond à ces filtres",
			"openSearch": "Ouvrir cette recherche sur CardNexus",
			"graded": "{service} {grade}",
			"sellerPro": "Vendeur professionnel",
			"sellerIndividual": "Vendeur particulier",
			"memberSince": "Membre depuis le {date}",
			"verifiedSales": "{count, plural, one {# vente vérifiée} other {# ventes vérifiées}}",
			"notTakingOrders": "Ne prend pas de commandes pour le moment",
			"completionRate": "{percent} des commandes récentes menées à terme",
			"shipsIn": "Expédie en {hours, plural, one {# heure environ} other {# heures environ}}",
			"sellerListings": "Annonces de {seller}",
			"listingOf": "Annonce de {name}",
			"sellerProfile": "Vendeur"
		},
		"cart": {
			"title": "Votre panier",
			"itemCount": "{count, plural, one {# article} other {# articles}}",
			"empty": "Votre panier est vide",
			"items": "Articles",
			"shipping": "Livraison",
			"total": "Total",
			"shippingNotQuoted": "Livraison non chiffrée",
			"reviewAndPay": "Vérifier et payer sur CardNexus",
			"removeNamed": "Retirer {name}",
			"increase": "Augmenter la quantité de {name}",
			"decrease": "Diminuer la quantité de {name}",
			"quantity": "Quantité {count}",
			"footnote": "Les totaux n’incluent ni les frais acheteur, ni la TVA, ni les promotions. Les prix définitifs s’affichent dans votre panier sur CardNexus.",
			"deliveryTo": "Livraison en {country}",
			"failures": "{count, plural, one {# annonce n’a pas été ajoutée} other {# annonces n’ont pas été ajoutées}}",
			"added": "Ajouté à votre panier",
			"updating": "Mise à jour du panier",
			"remove": "Retirer"
		},
		"plan": {
			"title": "Options de panier",
			"modeLowestPrice": "Prix le plus bas",
			"modeFewestSellers": "Moins de vendeurs",
			"modeBalanced": "Équilibré",
			"sellers": "{count, plural, one {# vendeur} other {# vendeurs}}",
			"items": "Articles",
			"shipping": "Livraison",
			"total": "Total",
			"unmet": "{count, plural, one {# carte n’a pas pu être trouvée} other {# cartes n’ont pas pu être trouvées}}",
			"replaceCart": "Remplacer mon panier par cette option",
			"replacing": "Remplacement du panier",
			"replaced": "Votre panier a été remplacé.",
			"openCart": "Ouvrir le panier",
			"openBuyList": "Ouvrir la liste d’achat sur CardNexus",
			"calculating": "Calcul de vos options",
			"checkAgain": "Vérifier à nouveau",
			"failed": "Le calcul du panier a échoué.",
			"noOptions": "Aucune option trouvée",
			"footnote": "Les prix définitifs s’affichent dans votre panier sur CardNexus."
		}
	}
};

//#endregion
//#region src/i18n/shared-translations.ts
const sharedTranslationsMap = {
	[Locale.English]: en_default,
	[Locale.French]: fr_default
};

//#endregion
export { ALL_COUNTRY_CODES, CURRENCY_FLAG_CODES, CURRENCY_SYMBOLS, Currency, EUR_COUNTRIES, LANGUAGE_FLAG_CODES, LOCALES_CURRENCY_MAP, Languages, Locale, SupportedMarketplaceCurrency, compactFromNow, config, configureDayjs, dayjs, defaultCurrencyForCountry, formatLanguageWithCode, formatUtc, fromNow, getAllCountries, getCountryName, getCurrencyForCountry, getLanguageDisplayName, humanizeDuration, isCurrencySymbol, resolveAvailableLanguage, sharedTranslationsMap };
//# sourceMappingURL=index.js.map